import { BaseError, type BaseErrorOptions } from '@canvas/error';

import { type GraphQLError } from './types';

/**
 * Error thrown when a GraphQL operation fails.
 * This is the main error type for the custom GraphQL client.
 */
type GraphQLBaseErrorOptions = Omit<BaseErrorOptions, 'name'> & { cause?: Error };

type GraphQLClientErrorOptions = GraphQLBaseErrorOptions & {
  graphQLErrors?: GraphQLError[];
};

export class GraphQLClientError extends BaseError {
  readonly graphQLErrors?: GraphQLError[];

  constructor(message: string, options?: GraphQLClientErrorOptions) {
    const { cause, ...baseOptions } = options ?? {};
    super(
      { name: 'GraphQLClientError', ...baseOptions },
      message,
      cause ? { cause } : undefined,
    );
    this.graphQLErrors = options?.graphQLErrors;
  }
}

/**
 * Error thrown when a network request fails.
 */
type GraphQLNetworkErrorOptions = GraphQLBaseErrorOptions & {
  statusCode?: number;
  response?: Response;
};

export class GraphQLNetworkError extends GraphQLClientError {
  readonly statusCode?: number;
  readonly response?: Response;

  constructor(message: string, options?: GraphQLNetworkErrorOptions) {
    super(message, options);
    this.name = 'GraphQLNetworkError';
    this.statusCode = options?.statusCode;
    this.response = options?.response;
  }
}

/**
 * Error thrown when retry attempts are exhausted.
 */
export class GraphQLRetryExhaustedError extends GraphQLClientError {
  readonly attempts: number;

  constructor(message: string, attempts: number, options?: GraphQLBaseErrorOptions) {
    super(message, options);
    this.name = 'GraphQLRetryExhaustedError';
    this.attempts = attempts;
  }
}

/**
 * Error thrown when a cache operation fails.
 */
export class GraphQLCacheError extends GraphQLClientError {
  constructor(message: string, options?: GraphQLBaseErrorOptions) {
    super(message, options);
    this.name = 'GraphQLCacheError';
  }
}

/**
 * Determines if an error is a GraphQL server error (contains graphQL errors in response).
 */
export function isGraphQLServerError(error: unknown): error is GraphQLClientError {
  return error instanceof GraphQLClientError && !!error.graphQLErrors?.length;
}

/**
 * Determines if an error is a network error.
 */
export function isGraphQLNetworkError(error: unknown): error is GraphQLNetworkError {
  return error instanceof GraphQLNetworkError;
}

/**
 * Determines if an error is retryable.
 * Network errors are retryable, but GraphQL server errors are not.
 */
export function isRetryableError(error: unknown): boolean {
  if (isGraphQLNetworkError(error)) {
    const statusCode = error.statusCode;
    if (statusCode !== undefined) {
      return (
        statusCode >= 500 || statusCode === 0 || statusCode === 408 || statusCode === 429
      );
    }
    return true;
  }
  return false;
}
